import { useQuery } from '@tanstack/react-query'

import { type AppTimezonePreference } from '@sparkai/utils'

import { apiClient } from '../apiClient'
import {
  getOperatorPerformanceQueryTimezonePart,
  getOperatorPerformanceRequestHeaders,
} from './operatorPerformanceTimezone'

type GetProgramStatsRequest = {
  operatorId?: string
  programIds: string[]
  startDate: string
  endDate: string
  timezonePreference?: AppTimezonePreference
}

type GetProgramStatsResponse = {
  tasks: {
    total: number
    audited: number
    trend: number
    auditedTrend: number
  }
  programs: {
    programId: string
    volume: {
      total: number
      totalCorrect: number
      totalIncorrect: number
      correctTrend: number
      trend: number
      data: { date: string; count: number; correct: number; incorrect: number }[]
    }
    accuracy: {
      mean: number
      trend: number
      data: { date: string; value: number }[]
    }

    responseTime: {
      mean: number
      trend: number
      data: { date: string; value: number }[]
    }
  }[]
}

export const useGetProgramStats = ({
  operatorId,
  programIds,
  startDate,
  endDate,
  timezonePreference,
}: GetProgramStatsRequest) => {
  const timezoneQueryKeyPart = getOperatorPerformanceQueryTimezonePart(timezonePreference)

  return useQuery({
    queryKey: [
      'operator-program-stats',
      operatorId,
      startDate,
      endDate,
      programIds,
      ...(timezoneQueryKeyPart ? [timezoneQueryKeyPart] : []),
    ],
    queryFn: async () => {
      const headers = getOperatorPerformanceRequestHeaders(timezonePreference)
      const { data } = await apiClient.get<GetProgramStatsResponse>(
        `/int/v3/operators/${operatorId}/program-stats`,
        {
          params: { startDate, endDate, programIds: programIds.join() },
          ...(headers ? { headers } : {}),
        }
      )
      return data
    },
    enabled: Boolean(operatorId) && programIds.length > 0,
    refetchOnWindowFocus: false,
  })
}
